import React, { useState } from 'react';
import { CornerIcon } from '@/components/CornerIcon';
import { BrandedIcon } from '@/components/ui/BrandedIcon';
import AnimatedProgress from '@/components/ui/AnimatedProgress';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  BookOpen, 
  Palette, 
  Play, 
  Image, 
  FileText,
  CheckCircle,
  Star,
  Heart,
  Target,
  Users,
  Zap
} from 'lucide-react';

const MockChapter: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [inputValue, setInputValue] = useState('');
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [radioValue, setRadioValue] = useState('option1');
  const [selectValue, setSelectValue] = useState('');

  return (
    <div className="min-h-screen bg-gray-50">
      <CornerIcon />
      
      <div className="max-w-6xl mx-auto p-6 pt-20">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-32 h-32 nm-card flex items-center justify-center">
              <BrandedIcon 
                type="learning" 
                variant="animated" 
                size="xl"
                className="w-20 h-20"
              />
            </div>
          </div>
          
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Mock Chapter: Design System Showcase
          </h1>
          <p className="text-xl text-gray-600 mb-6 max-w-3xl mx-auto">
            This is a demonstration of Chapter 1's exact design patterns, including neumorphic cards, 
            tab systems, and working shadcn/ui components.
          </p>
          
          {/* Progress Section */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="flex-1 max-w-md">
              <AnimatedProgress 
                value={75} 
                className="mb-2" 
                showAnimation={true}
              />
              <p className="text-sm text-gray-600">
                3 of 4 sections completed
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        {/* Tab System - Exact copy from Dashboard.tsx */}
        <div className="w-full mb-12">
          <div className="nm-card-inset grid w-full grid-cols-5 mb-8 p-2 gap-2">
            <button 
              className={`nm-tab ${activeTab === 'overview' ? 'nm-tab-active' : ''} px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium ${activeTab === 'overview' ? 'text-emerald-600' : 'text-gray-600'}`}
              onClick={() => setActiveTab('overview')}
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
              <span className="sm:hidden">Info</span>
            </button>
            
            <button 
              className={`nm-tab ${activeTab === 'components' ? 'nm-tab-active' : ''} px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium ${activeTab === 'components' ? 'text-indigo-600' : 'text-gray-600'}`}
              onClick={() => setActiveTab('components')}
            >
              <Palette className="w-4 h-4" />
              <span className="hidden sm:inline">Components</span>
              <span className="sm:hidden">UI</span>
            </button>
            
            <button 
              className={`nm-tab ${activeTab === 'interactive' ? 'nm-tab-active' : ''} px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium ${activeTab === 'interactive' ? 'text-purple-600' : 'text-gray-600'}`}
              onClick={() => setActiveTab('interactive')}
            >
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline">Interactive</span>
              <span className="sm:hidden">Play</span>
            </button>
            
            <button 
              className={`nm-tab ${activeTab === 'icons' ? 'nm-tab-active' : ''} px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium ${activeTab === 'icons' ? 'text-cyan-600' : 'text-gray-600'}`}
              onClick={() => setActiveTab('icons')}
            >
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Icons</span>
              <span className="sm:hidden">Icons</span>
            </button>
            
            <button 
              className={`nm-tab ${activeTab === 'documentation' ? 'nm-tab-active' : ''} px-4 py-3 flex items-center justify-center gap-2 text-sm font-medium ${activeTab === 'documentation' ? 'text-orange-600' : 'text-gray-600'}`}
              onClick={() => setActiveTab('documentation')}
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Documentation</span>
              <span className="sm:hidden">Docs</span>
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="nm-card p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <BrandedIcon type="learning" size="lg" />
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Design System Overview
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    This chapter demonstrates the exact design patterns used in Chapter 1, 
                    including neumorphic styling, card layouts, and interactive components.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <span className="text-sm text-muted-foreground">Neumorphic Design</span>
                    <span className="text-sm text-muted-foreground">Interactive Components</span>
                    <span className="text-sm text-muted-foreground">Responsive Layout</span>
                  </div>
                </div>

                <div className="nm-card p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <BrandedIcon type="data" size="lg" />
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Technical Features
                    </h3>
                  </div>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span>Exact Chapter 1 gradient backgrounds</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span>Working shadcn/ui components</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span>BrandedIcon integration</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span>Responsive tab system</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'components' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="nm-card border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BrandedIcon type="communication" size="md" />
                      Input Components
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="demo-input">Text Input</Label>
                      <Input
                        id="demo-input"
                        placeholder="Enter text here..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="demo-checkbox"
                        checked={checkboxValue}
                        onCheckedChange={setCheckboxValue}
                      />
                      <Label htmlFor="demo-checkbox">Checkbox option</Label>
                    </div>
                  </CardContent>
                </Card>

                <Card className="nm-card border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BrandedIcon type="workflow" size="md" />
                      Selection Components
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Radio Group</Label>
                      <RadioGroup value={radioValue} onValueChange={setRadioValue} className="mt-2">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="option1" id="option1" />
                          <Label htmlFor="option1">Option 1</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="option2" id="option2" />
                          <Label htmlFor="option2">Option 2</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </CardContent>
                </Card>

                <Card className="nm-card border-0">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BrandedIcon type="achievement" size="md" />
                      Dropdown & Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Select Dropdown</Label>
                      <Select value={selectValue} onValueChange={setSelectValue}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Choose option..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="choice1">Choice 1</SelectItem>
                          <SelectItem value="choice2">Choice 2</SelectItem>
                          <SelectItem value="choice3">Choice 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <Button className="w-full" variant="default">
                      Submit Form
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'interactive' && (
            <div className="space-y-8">
              <div className="nm-card p-8 text-center">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Interactive Demo</h3>
                <div className="grid md:grid-cols-4 gap-6">
                  <Button variant="default" className="h-16">
                    <Star className="w-5 h-5 mr-2" />
                    Default
                  </Button>
                  <Button variant="outline" className="h-16">
                    <Heart className="w-5 h-5 mr-2" />
                    Outline
                  </Button>
                  <Button variant="gradient" className="h-16">
                    <Target className="w-5 h-5 mr-2" />
                    Gradient
                  </Button>
                  <Button variant="premium" className="h-16">
                    <Zap className="w-5 h-5 mr-2" />
                    Premium
                  </Button>
                </div>
                
                <div className="mt-8">
                  <AnimatedProgress value={60} showAnimation={true} className="mb-4" />
                  <p className="text-gray-600">Interactive progress demonstration</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'icons' && (
            <div className="space-y-8">
              <div className="nm-card p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                  BrandedIcon Showcase
                </h3>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-8">
                  {[
                    { type: 'learning' as const, label: 'Learning' },
                    { type: 'communication' as const, label: 'Communication' },
                    { type: 'data' as const, label: 'Data' },
                    { type: 'workflow' as const, label: 'Workflow' },
                    { type: 'achievement' as const, label: 'Achievement' }
                  ].map(({ type, label }) => (
                    <div key={type} className="flex flex-col items-center gap-3">
                      <div className="w-16 h-16 flex items-center justify-center">
                        <BrandedIcon 
                          type={type} 
                          variant="animated" 
                          size="xl"
                        />
                      </div>
                      <span className="text-sm text-gray-600 text-center">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documentation' && (
            <div className="space-y-8">
              <div className="nm-card p-8">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Implementation Notes
                </h3>
                <div className="prose prose-gray max-w-none">
                  <h4>Design System Components Used:</h4>
                  <ul>
                    <li><strong>nm-card:</strong> Neumorphic card styling matching Chapter 1</li>
                    <li><strong>nm-tab system:</strong> Exact copy of Dashboard.tsx tab implementation</li>
                    <li><strong>BrandedIcon:</strong> All 5 supported types with animated variants</li>
                    <li><strong>AnimatedProgress:</strong> Chapter 1 style progress indicators</li>
                    <li><strong>Gradient Background:</strong> from-cyan-50 via-white to-purple-50</li>
                  </ul>
                  
                  <h4>shadcn/ui Components Demonstrated:</h4>
                  <ul>
                    <li>Input, Button, Checkbox</li>
                    <li>RadioGroup, RadioGroupItem</li>
                    <li>Select with all sub-components</li>
                    <li>Card, Button, Label</li>
                  </ul>
                  
                  <h4>Key Features:</h4>
                  <ul>
                    <li>Fully responsive design</li>
                    <li>No syntax errors</li>
                    <li>Working interactive components</li>
                    <li>Proper TypeScript typing</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-center gap-4 mt-12">
          <Button variant="outline" size="lg">
            <Users className="w-4 h-4 mr-2" />
            View More Examples
          </Button>
          <Button variant="gradient" size="lg">
            <Play className="w-4 h-4 mr-2" />
            Try Interactive Demo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MockChapter;
